import { getBills } from "../../api/bills/bills";
import getRequiredUserData from "../../utils/getBranchInfo";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  useTheme,
  Typography,
  Card,
  Divider,
  CardContent,
} from "@mui/material";
import Header from "../../components/VersatileComponents/Header";
import { salaryColumn } from "../../utils/tableColumns";
import DataTable from "../../components/VersatileComponents/DataTable";
import useTableData from "../../hooks/useTableData";
import BonusDialog from "../../components/BonusPenality/Bonus";
import { useAuth } from "../../contexts/AuthContext";
import useUserClaims from "../../hooks/useUserClaims";
import { Helmet } from "react-helmet-async";
import NewExpenseTable from "../../components/Expense/NewExpense";
import { ExportToExcel } from "../../utils/ExportToExcel";
import useDocumentById from "../../hooks/useDocumentById";
import getData from "../../api/services/DeliveryGuy/getDeliveryGuy";
import { styled } from "@mui/system";
import generateCustomID from "../../utils/generateCustomID";

const containerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  margin: "1rem",
};

const flexItemStyle = {
  flex: 13,
};

const flexItemStyles = {
  flex: 1,
};

const StyledSummaryCard = styled(Card)(({ theme }) => ({
  borderRadius: "1.5rem",
  backgroundColor: theme.palette.background.alt,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  transition: "transform 0.2s ease-in-out",
  position: "relative",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  width: "40%",
  minWidth: 320,
  maxWidth: 600,
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
  },
}));

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography color="text.primary" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const SalaryPage = () => {
  let active = "";
  let salaryTable = [];
  let branchId = "";
  let tsf;
  let ts;

  const storedData = localStorage.getItem("userData");
  if (storedData) {
    const userData = JSON.parse(storedData);
    active = userData.active !== undefined ? userData.active : "";
    salaryTable =
      userData.salaryTable !== undefined ? userData.salaryTable : [];
    branchId = userData.id !== undefined ? userData.id : [];
  }

  // Bill Records Table State
  const [billData, setBillData] = useState([]);
  const [billLastDoc, setBillLastDoc] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billSearchedData, setBillSearchedData] = useState([]);
  const [selectedBillTab, setSelectedBillTab] = useState(active);
  const userData = getRequiredUserData();

  // Only show these columns for Bill Records
  const billColumns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "reason", headerName: "Reason", flex: 2 },
    { field: "type", headerName: "Type", flex: 1 },
    { field: "amount", headerName: "Amount", flex: 1 },
  ];

  // Helper to format date (copied from BillTable)
  const formatDate = (dateValue) => {
    try {
      if (!dateValue) return "N/A";
      let date;
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === "string") {
        date = new Date(dateValue);
      } else if (typeof dateValue === "number") {
        date = new Date(dateValue);
      } else {
        return "N/A";
      }
      if (isNaN(date.getTime())) {
        return "N/A";
      }
      return format(date, "MMM dd, yyyy");
    } catch (error) {
      return "N/A";
    }
  };

  // Process data for Bill Records Table
  const processBillData = (dataArray) => {
    return dataArray.map((item) => ({
      ...item,
      date: formatDate(item.date),
    }));
  };

  // Load Bill Records
  useEffect(() => {
    if (!userData.requiredId || !selectedBillTab) return;
    setBillLoading(true);
    const unsubscribe = getBills(
      userData.requiredId,
      (bills) => {
        setBillData(bills);
        setBillLoading(false);
        if (bills.length > 0) setBillLastDoc(bills[bills.length - 1]);
      },
      null,
      100,
      [],
      null,
      selectedBillTab
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userData.requiredId, selectedBillTab]);

  const handleBillTabChange = (event, newValue) => {
    setSelectedBillTab(newValue);
  };

  const theme = useTheme();
  const { user } = useAuth();
  const userClaims = useUserClaims(user);
  const staffSalaryColumn = [
    {
      field: "uniqueName",
      headerName: "Unique Name",
      flex: 0.4,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "bonus",
      headerName: "Bonus",
      flex: 0.4,
    },
    {
      field: "addbonus",
      headerName: "Give Holiday Bonus",
      flex: 0.6,
      renderCell: (params) =>
        params.row.addbonus ? (
          <BonusDialog worker={"individual"} id={params.row.id} />
        ) : (
          <div></div>
        ),
    },
    {
      field: "penality",
      headerName: "Penality",
      flex: 0.4,
    },
    {
      field: "fixedSalary",
      headerName: "Fixed Salary",
      flex: 0.4,
    },
    {
      field: "holidayBonus",
      headerName: "Holiday Bonus",
      flex: 0.4,
    },
    {
      field: "fundraising",
      headerName: "Fundraising",
      flex: 0.4,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 0.4,
    },
  ];

  const staffSalaryColumnForNonAdmin = [
    {
      field: "uniqueName",
      headerName: "Unique Name",
      flex: 0.4,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "bonus",
      headerName: "Bonus",
      flex: 0.4,
    },
    {
      field: "penality",
      headerName: "Penality",
      flex: 0.4,
    },
    {
      field: "fixedSalary",
      headerName: "Fixed Salary",
      flex: 0.4,
    },
    {
      field: "holidayBonus",
      headerName: "Holiday Bonus",
      flex: 0.4,
    },
    {
      field: "fundraising",
      headerName: "Fundraising",
      flex: 0.4,
    },
    {
      field: "totalCredit",
      headerName: "Total Credit",
      flex: 0.4,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 0.4,
    },
  ];

  if (!active) {
    active = "abcedafkas";
  }
  const [selectedTab, setSelectedTab] = useState(active);
  const [selectedTab2, setSelectedTabTwo] = useState(active);
  const [selectedSummaryTab, setSelectedSummaryTab] = useState(active);

  const { data: salary } = useTableData(selectedTab, "salary");
  const { data: staffSalary } = useTableData(selectedTab2, "staffSalary");

  // Fetch data for StyledSummaryCard
  const { documentData: documentData2 } = useDocumentById("Budget", branchId);
  const { documentData: finance } = useDocumentById("finance", user.uid);
  const { documentData: status } = useDocumentById(
    "Status",
    selectedSummaryTab ? selectedSummaryTab : branchId
  );
  const { documentData: bank } = useDocumentById("Bank", branchId);
  const customerCreditTotalId = generateCustomID(
    `${userData.requiredId}-customer`
  );
  const staffCreditTotalId = generateCustomID(`${userData.requiredId}-staff`);
  const dailyCreditTotalId = generateCustomID(`${userData.requiredId}-daily`);
  const { documentData: totalCustomerCredit } = useDocumentById(
    "totalCredit",
    customerCreditTotalId
  );
  const { documentData: totalStaffCredit } = useDocumentById(
    "totalCredit",
    staffCreditTotalId
  );
  const { documentData: totalDailyCredit } = useDocumentById(
    "totalCredit",
    dailyCreditTotalId
  );
  const { data: table } = useTableData(selectedSummaryTab || "dummy");

  const [calculator, setCalculator] = useState({
    sum: 0,
    actual: 0,
    balance: 0,
  });

  useEffect(() => {
    if (!selectedSummaryTab) return;
    const unsubscribe = getData(
      "Calculator",
      "active",
      selectedSummaryTab,
      setCalculator
    );
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [selectedSummaryTab]);

  useEffect(() => {
    if (!salary || !staffSalary) {
      return;
    }
    ts = salary.find((item) => item.name === "total")?.totalCredit || 0;
    tsf = staffSalary.find((item) => item.name === "total")?.totalCredit || 0;
  }, [salary, staffSalary]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const handleTabChange2 = (event, newValue) => {
    setSelectedTabTwo(newValue);
  };
  const handleSummaryTabChange = (event, newValue) => {
    setSelectedSummaryTab(newValue);
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Salary</title>
        <meta name="description" content="Salary pages" />
      </Helmet>
      {active !== "abcedafkas" ? (
        <Box
          m="1.5rem 2.5rem"
          sx={{
            backgroundColor: theme.palette.background.default,
            height: "100%",
            position: "relative",
          }}
        >
          <Header title="Delivery guys Salary Table" subtitle="" />
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            style={{
              color: theme.palette.secondary[700],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            {salaryTable.map((tabData) => (
              <Tab
                key={tabData.id}
                label={tabData.name}
                value={tabData.id}
                style={{
                  color: theme.palette.secondary[300],
                  ...(selectedTab === tabData.id && {
                    color: theme.palette.secondary[100],
                    borderBottom: `5px solid ${theme.palette.grey[900]}`,
                  }),
                }}
              />
            ))}
          </Tabs>

          <div style={containerStyle}>
            <div style={flexItemStyle}></div>
            <div style={flexItemStyles}>
              {userClaims.superAdmin || userClaims.finance ? (
                <ExportToExcel
                  file={"salary"}
                  branchId={"non"}
                  id={active}
                  endpoint={"delSal"}
                  clear={false}
                  name="DeliverySalaryTable"
                />
              ) : null}
            </div>
          </div>

          {salary.length > 0 && (
            <DataTable rows={salary} columns={salaryColumn} />
          )}

          <Header title="Staff Salary Table" subtitle="" />
          <Tabs
            value={selectedTab2}
            onChange={handleTabChange2}
            variant="scrollable"
            scrollButtons="auto"
            style={{
              color: theme.palette.secondary[700],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            {salaryTable.map((tabData) => (
              <Tab
                key={tabData.id}
                label={tabData.name}
                value={tabData.id}
                style={{
                  color: theme.palette.secondary[300],
                  ...(selectedTab2 === tabData.id && {
                    color: theme.palette.secondary[100],
                    borderBottom: `5px solid ${theme.palette.grey[900]}`,
                  }),
                }}
              />
            ))}
          </Tabs>

          <div style={containerStyle}>
            <div style={flexItemStyle}></div>
            <div style={flexItemStyles}>
              {userClaims.superAdmin || userClaims.finance ? (
                <ExportToExcel
                  file={"staffSalary"}
                  branchId={branchId}
                  id={active}
                  endpoint={"staffsalary"}
                  clear={false}
                  name="StaffSalaryTable"
                />
              ) : null}
            </div>
          </div>

          {staffSalary.length > 0 && (
            <DataTable
              rows={staffSalary}
              columns={
                userClaims.admin
                  ? staffSalaryColumn
                  : staffSalaryColumnForNonAdmin
              }
            />
          )}

          <Header title="Bill Records" subtitle="" />
          <Tabs
            value={selectedBillTab}
            onChange={handleBillTabChange}
            variant="scrollable"
            scrollButtons="auto"
            style={{
              color: theme.palette.secondary[700],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            {salaryTable.map((tabData) => (
              <Tab
                key={tabData.id}
                label={tabData.name}
                value={tabData.id}
                style={{
                  color: theme.palette.secondary[300],
                  ...(selectedBillTab === tabData.id && {
                    color: theme.palette.secondary[100],
                    borderBottom: `5px solid ${theme.palette.grey[900]}`,
                  }),
                }}
              />
            ))}
          </Tabs>
          <div style={{ marginBottom: "1.5rem" }}>
            <DataTable
              rows={processBillData(billData)}
              columns={billColumns}
              loading={billLoading}
            />
          </div>

          <Header title="Current Expense Table" subtitle="" />
          <div style={containerStyle}>
            <div style={flexItemStyle}></div>
            <div style={flexItemStyles}>
              {userClaims.superAdmin || userClaims.finance ? (
                <ExportToExcel
                  file={"Status"}
                  branchId={branchId}
                  id={active}
                  endpoint={"sheetstatus"}
                  clear={false}
                  name="SheetStatusTable"
                />
              ) : null}
            </div>
          </div>

          {branchId ? <NewExpenseTable id={active} /> : null}

          <Header title="Summary" subtitle="" />
          <Tabs
            value={selectedSummaryTab}
            onChange={handleSummaryTabChange}
            variant="scrollable"
            scrollButtons="auto"
            style={{
              color: theme.palette.secondary[700],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            {salaryTable.map((tabData) => (
              <Tab
                key={tabData.id}
                label={tabData.name}
                value={tabData.id}
                style={{
                  color: theme.palette.secondary[300],
                  ...(selectedSummaryTab === tabData.id && {
                    color: theme.palette.secondary[100],
                    borderBottom: `5px solid ${theme.palette.grey[900]}`,
                  }),
                }}
              />
            ))}
          </Tabs>

          {selectedSummaryTab && (
            <StyledSummaryCard>
              <CardContent sx={{ p: 5 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Summary
                  </Typography>
                  <Typography
                    variant="h4"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    {userData?.branchName || "Branch"}
                  </Typography>
                </Box>
                <InfoRow
                  label="Total sales"
                  value={
                    status?.totalIncome !== undefined
                      ? Number(status.totalIncome).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Total Salary Cost"
                  value={
                    status?.totalStaffSalary !== undefined
                      ? Number(status.totalStaffSalary).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Total Expense"
                  value={
                    status?.totalExpense !== undefined
                      ? Number(status.totalExpense).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Status amount"
                  value={(() => {
                    const totalSales =
                      status?.totalIncome !== undefined
                        ? Number(status.totalIncome)
                        : undefined;
                    const totalExpense =
                      status?.totalExpense !== undefined
                        ? Number(status.totalExpense)
                        : undefined;
                    if (
                      totalSales !== undefined &&
                      totalExpense !== undefined
                    ) {
                      return (totalSales - totalExpense).toFixed(2);
                    }
                    return "Not available";
                  })()}
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Status"
                  value={(() => {
                    const totalSales =
                      status?.totalIncome !== undefined
                        ? status.totalIncome
                        : undefined;
                    const totalExpense =
                      status?.totalExpense !== undefined
                        ? status.totalExpense
                        : undefined;
                    if (
                      totalSales !== undefined &&
                      totalExpense !== undefined
                    ) {
                      return parseFloat(totalSales) - parseFloat(totalExpense) >
                        0
                        ? "Profit"
                        : "Loss";
                    }
                    return "Not available";
                  })()}
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Total staff credit"
                  value={
                    totalStaffCredit?.staffCreditTotal !== undefined
                      ? Number(totalStaffCredit.staffCreditTotal).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Total customer credit"
                  value={
                    totalCustomerCredit?.customerCreditTotal !== undefined
                      ? Number(totalCustomerCredit.customerCreditTotal).toFixed(
                          2
                        )
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Cash on bank"
                  value={
                    bank?.total !== undefined
                      ? Number(bank.total).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Cash on Hand"
                  value={
                    calculator && calculator.sum !== undefined
                      ? Number(calculator.sum).toFixed(2)
                      : "Not available"
                  }
                />
                <Divider sx={{ my: 1 }} />
                <InfoRow
                  label="Balance"
                  value={(() => {
                    if (
                      calculator &&
                      calculator.sum !== undefined &&
                      documentData2 &&
                      bank
                    ) {
                      return documentData2 && calculator
                        ? (
                            calculator.sum.toFixed(2) -
                            (
                              documentData2.budget +
                              (table[table.length - 1]?.total -
                                ((totalCustomerCredit?.customerCreditTotal ||
                                  0) +
                                  (totalDailyCredit.dailyCreditTotal || 0) +
                                  (totalStaffCredit.staffCreditTotal || 0))) -
                              (bank.total || 0)
                            )?.toFixed(2)
                          )?.toFixed(2)
                        : "Not available";
                    }
                    return "Not available";
                  })()}
                />
              </CardContent>
            </StyledSummaryCard>
          )}
        </Box>
      ) : null}
    </>
  );
};

export default SalaryPage;
